import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewContainerRef,
  input,
  viewChild,
} from '@angular/core';
import { Observable, ReplaySubject, Subject, of, takeUntil } from 'rxjs';
import { CustomDotCmsContentlet } from 'src/app/extensions/dot-cms/models/contentlet/custom-dotcms-contentlet.model';
import { configureSlides, configureSwiper } from 'src/app/extensions/dot-cms/utils/dot-cms-utils';
import SwiperCore, { Autoplay, Navigation, Pagination, SwiperOptions } from 'swiper';

import {
  LARGE_BREAKPOINT_WIDTH,
  MEDIUM_BREAKPOINT_WIDTH,
  SMALL_BREAKPOINT_WIDTH,
} from 'ish-core/configurations/injection-keys';
import { CategoryView } from 'ish-core/models/category-view/category-view.model';
import { InjectSingle } from 'ish-core/utils/injection';

import { DotCmsCategorySwiperComponent } from '../dot-cms-category-swiper/dot-cms-category-swiper.component';

/**
 * The Category Tile Component renders a category tile with the image of the
 * category using {@link CategoryImageComponent}.
 *
 * @example
 * <ish-category-tile [categoryUniqueId]="category"></ish-category-tile>
 */

SwiperCore.use([Autoplay, Navigation, Pagination]);

export interface DotCmsDynamicCategories {
  swiperConfig?: SwiperOptions;
  contentlet?: CustomDotCmsContentlet;
  categories: CategoryView[];
}
@Component({
  selector: 'custom-dot-cms-category-tile',
  templateUrl: './dot-cms-category-tile.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DotCmsCategoryTileComponent implements OnChanges, OnDestroy, OnInit {
  /**
   * The Category to render a tile for
   */
  readonly categories = input<CategoryView[]>(undefined);
  readonly contentlet = input<CustomDotCmsContentlet>(undefined);

  readonly categoryCarousel = viewChild('categoryCarousel', { read: ViewContainerRef });
  categories$: Observable<CategoryView[]>;
  dotCmsDynamicCategories$ = new ReplaySubject<DotCmsDynamicCategories>(1);
  private destroy$ = new Subject<void>();

  breakPointWidths: number[] = [];
  swiperConfig: SwiperOptions;

  constructor(
    @Inject(SMALL_BREAKPOINT_WIDTH) private smallBreakpointWidth: InjectSingle<typeof SMALL_BREAKPOINT_WIDTH>,
    @Inject(MEDIUM_BREAKPOINT_WIDTH) private mediumBreakpointWidth: InjectSingle<typeof MEDIUM_BREAKPOINT_WIDTH>,
    @Inject(LARGE_BREAKPOINT_WIDTH) private largeBreakpointWidth: InjectSingle<typeof LARGE_BREAKPOINT_WIDTH>
  ) {
    this.swiperConfig = {
      direction: 'horizontal',
      navigation: true,
      observer: true,
      observeParents: true,
    };
    this.breakPointWidths = [this.smallBreakpointWidth, this.mediumBreakpointWidth, this.largeBreakpointWidth];
  }

  ngOnInit(): void {
    this.dotCmsDynamicCategories$.pipe(takeUntil(this.destroy$)).subscribe(dotCmsCategoriesContentlet => {
      this.createDynamicSwiper(this.rearrangeCategories(dotCmsCategoriesContentlet));
    });
  }
  ngOnChanges() {
    this.categories$ = of(this.categories());

    //Note: Swiper Carousel may break if you try to set the configuration dynamically to avoid such brakage we just used four observable which sets the swiper config only once.
    this.categories$.pipe(takeUntil(this.destroy$)).subscribe(categories => {
      const contentlet = this.contentlet();
      if (contentlet.slideItems !== 0 && categories.length > contentlet.slideItems) {
        Object.entries(configureSwiper(contentlet, this.breakPointWidths)).map(([key, value]) => {
          //@ts-ignore
          this.swiperConfig[key] = value;
        });
        this.dotCmsDynamicCategories$.next({
          contentlet,
          swiperConfig: this.swiperConfig,
          categories,
        });
      } else {
        this.swiperConfig.breakpoints = configureSlides(this.categories().length, this.breakPointWidths);
        this.dotCmsDynamicCategories$.next({
          contentlet,
          swiperConfig: this.swiperConfig,
          categories,
        });
      }
    });
  }
  createDynamicSwiper(dotCmsCategories: DotCmsDynamicCategories) {
    const scrollY = !SSR ? window.scrollY || document.documentElement.scrollTop : 0;

    this.categoryCarousel().clear();
    const componentRef = this.categoryCarousel().createComponent(DotCmsCategorySwiperComponent);
    componentRef.setInput('dynamicCategory', dotCmsCategories);
    if (!SSR && scrollY !== 0) {
      requestAnimationFrame(() => {
        window.scrollTo({
          top: scrollY,
          behavior: 'instant' as ScrollBehavior,
        });
      });
    }
  }

  rearrangeCategories(dynamicCategories: DotCmsDynamicCategories) {
    if (
      dynamicCategories.contentlet.loop &&
      dynamicCategories.contentlet.slideItems !== 0 &&
      dynamicCategories.categories.length > this.contentlet().slideItems &&
      dynamicCategories.categories.length % this.contentlet().slideItems !== 0
    ) {
      let categoriesIn = dynamicCategories.categories;
      const sliceval =
        this.contentlet().slideItems - (dynamicCategories.categories.length % this.contentlet().slideItems);
      categoriesIn = [...categoriesIn, ...categoriesIn.slice(0, sliceval)];
      dynamicCategories.categories = categoriesIn;
    }
    return dynamicCategories;
  }

  categoryCarouselCSS() {
    const configureSlides =
      this.contentlet().slideItems < this.categories().length ? this.contentlet().slideItems : this.categories().length;
    switch (configureSlides) {
      case 1: {
        return 'swiper-one-slide';
      }
      case 2: {
        return 'swiper-two-slides';
      }
      case 3: {
        return 'swiper-three-slides';
      }
      default: {
        return 'swiper-four-slides';
      }
    }
  }
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.dotCmsDynamicCategories$.complete();
  }
}
